
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Clipboard } from 'lucide-react';
import { CampaignSettings, LeaderboardSettings } from '../types';
import CampaignWidgetPreview from '../CampaignWidgetPreview';
import LeaderboardPreview from '../LeaderboardPreview';
import { FormProvider, useForm } from 'react-hook-form';

interface CampaignPreviewProps {
  settings: CampaignSettings;
  onActivate: (settings: CampaignSettings) => void;
  campaignType?: string;
}

const CampaignPreview: React.FC<CampaignPreviewProps> = ({
  settings,
  onActivate,
  campaignType
}) => {
  const [isCopied, setIsCopied] = React.useState(false);
  
  // Initialize form to ensure components inside have access to the context
  const form = useForm({
    defaultValues: settings
  });

  const handleCopyCode = () => {
    // Mock copy functionality - in a real app, you would use the Clipboard API
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Create a default LeaderboardSettings object for when it's undefined
  const defaultLeaderboardSettings: LeaderboardSettings = {
    contestPeriod: 'monthly',
    startDate: new Date(),
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    visibilityType: 'public',
    topPositionsCount: 10,
    firstPlacePrize: '100',
    secondPlacePrize: '50',
    thirdPlacePrize: '25',
    prizeType: 'cash',
    minimumReferrals: 3,
    showRealTimeUpdates: true,
    allowTies: true,
    displayUserProfiles: true
  };

  const renderCampaignPreview = () => {
    if (campaignType === 'leaderboard') {
      return <LeaderboardPreview settings={settings.leaderboardSettings || defaultLeaderboardSettings} />;
    }
    return <CampaignWidgetPreview settings={settings} />;
  };

  const renderPreviewSection = () => {
    return (
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-4">Preview</h3>
        <div className="border rounded-lg p-6 bg-gray-50">
          <div className="max-w-md mx-auto">
            {renderCampaignPreview()}
          </div>
        </div>
      </div>
    );
  };

  const renderSummarySection = () => {
    const isAffiliateProgram = campaignType === 'affiliate-program';
    const isLeaderboard = campaignType === 'leaderboard';
    const isNewsletterReferral = campaignType === 'newsletter-referral';
    
    return (
      <div>
        <h3 className="text-lg font-medium mb-4">Summary</h3>
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">{settings.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-sm">Description</h4>
              <p className="text-gray-500 text-sm">{settings.subtitle}</p>
            </div>

            {!isAffiliateProgram && !isLeaderboard && !isNewsletterReferral && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm">Referrer Reward</h4>
                  <p className="text-gray-500 text-sm">{settings.referrerReward} {settings.currency}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Referee Reward</h4>
                  <p className="text-gray-500 text-sm">{settings.refereeReward} {settings.currency}</p>
                </div>
              </div>
            )}

            {isAffiliateProgram && settings.affiliateSettings && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm">Commission Rate</h4>
                  <p className="text-gray-500 text-sm">
                    {settings.affiliateSettings.commissionType === 'percentage' 
                      ? `${settings.affiliateSettings.commissionRate}%` 
                      : `${settings.affiliateSettings.commissionRate} ${settings.currency}`
                    }
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Payout Frequency</h4>
                  <p className="text-gray-500 text-sm">
                    {settings.affiliateSettings.payoutFrequency === 'biweekly' 
                      ? 'Bi-weekly' 
                      : settings.affiliateSettings.payoutFrequency.charAt(0).toUpperCase() + 
                        settings.affiliateSettings.payoutFrequency.slice(1)
                    }
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Min. Payout</h4>
                  <p className="text-gray-500 text-sm">{settings.affiliateSettings.minimumPayout} {settings.currency}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Payout Method</h4>
                  <p className="text-gray-500 text-sm">
                    {settings.affiliateSettings.payoutMethod === 'bank' 
                      ? 'Bank Transfer' 
                      : settings.affiliateSettings.payoutMethod.charAt(0).toUpperCase() + 
                        settings.affiliateSettings.payoutMethod.slice(1)
                    }
                  </p>
                </div>
              </div>
            )}

            {isLeaderboard && settings.leaderboardSettings && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm">Contest Period</h4>
                  <p className="text-gray-500 text-sm">
                    {settings.leaderboardSettings.contestPeriod.charAt(0).toUpperCase() + 
                      settings.leaderboardSettings.contestPeriod.slice(1)}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Top Positions</h4>
                  <p className="text-gray-500 text-sm">{settings.leaderboardSettings.topPositionsCount}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">First Place Prize</h4>
                  <p className="text-gray-500 text-sm">{settings.leaderboardSettings.firstPlacePrize} ({settings.leaderboardSettings.prizeType})</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Minimum to Qualify</h4>
                  <p className="text-gray-500 text-sm">{settings.leaderboardSettings.minimumReferrals} referrals</p>
                </div>
              </div>
            )}

            {isNewsletterReferral && settings.newsletterSettings && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm">Email Provider</h4>
                  <p className="text-gray-500 text-sm">{settings.newsletterSettings.emailProvider}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Count Type</h4>
                  <p className="text-gray-500 text-sm">
                    {settings.newsletterSettings.referralCountType === 'signup' 
                      ? 'On Signup' 
                      : settings.newsletterSettings.referralCountType === 'confirmed' 
                        ? 'After Confirmation' 
                        : 'After Payment'
                    }
                  </p>
                </div>
              </div>
            )}

            <div>
              <h4 className="font-medium text-sm">Share Options</h4>
              <div className="flex flex-wrap gap-2 mt-1">
                {settings.shareOptions.map(option => (
                  <div key={option} className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-2 border-t">
              <h4 className="font-medium text-sm mb-2">Widget Code</h4>
              <div className="relative bg-gray-50 border rounded-md p-3">
                <pre className="text-xs overflow-x-auto">
                  {`<script src="https://refer.me/widget/${settings.globalCampaignId}" async></script>`}
                </pre>
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-2 right-2"
                  onClick={handleCopyCode}
                >
                  {isCopied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Clipboard className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Add this code to your website to display the referral widget
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end mt-6">
          <Button onClick={() => onActivate(settings)} size="lg">
            Activate Campaign
          </Button>
        </div>
      </div>
    );
  };

  return (
    <FormProvider {...form}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          {renderPreviewSection()}
        </div>
        <div>
          {renderSummarySection()}
        </div>
      </div>
    </FormProvider>
  );
};

export default CampaignPreview;
